import React, { useEffect, useState } from 'react';
import teamService from '../../services/teamService';
import AnalyticsDashboard from '../analytics/AnalyticsDashboard';
import SettingsPanel from '../settings/SettingsPanel';
import TestPanel from '../testing/TestPanel';
import TeamsManagement from './components/TeamsManagement';
import AdminOverviewSection from './components/AdminOverviewSection';
import './AdminPanel.css';

const AdminPanel = ({ theme, onThemeChange, csvData, onBackToMain }) => {
  const [teamsData, setTeamsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentSection, setCurrentSection] = useState('dashboard');

  const loadTeamsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const isHealthy = await teamService.checkApiHealth();
      if (!isHealthy) {
        setTeamsData([]);
        setError('API сервер недоступен. Проверьте подключение к серверу.');
        return;
      }

      const teamsStats = await teamService.getTeamsStats();
      const teamsWithData = await Promise.all(
        teamsStats.map(async (team) => {
          try {
            const teamDetails = await teamService.getTeam(team.name);
            return {
              ...team,
              steps: teamDetails.steps || [],
            };
          } catch (loadError) {
            console.error(`Ошибка загрузки данных команды ${team.name}:`, loadError);
            return {
              ...team,
              steps: [],
            };
          }
        })
      );

      setTeamsData(teamsWithData);
    } catch (loadError) {
      console.error('Ошибка загрузки данных команд:', loadError);
      setError('Ошибка загрузки данных команд');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTeamsData();
  }, []);

  const navItems = [
    { id: 'dashboard', icon: '📊', label: 'Дашборды' },
    { id: 'teams', icon: '👥', label: 'Команды' },
    { id: 'analytics', icon: '📈', label: 'Аналитика' },
    { id: 'settings', icon: '⚙️', label: 'Настройки' },
    { id: 'testing', icon: '🧪', label: 'Тестирование' },
  ];

  if (loading) {
    return (
      <div className={`admin-panel ${theme === 'dark' ? 'dark-theme' : ''}`}>
        <div className="admin-loading-state">
          <div className="admin-loading-ring" />
          <p>Загрузка данных команд...</p>
        </div>
      </div>
    );
  }

  if (error && teamsData.length === 0) {
    return (
      <div className={`admin-panel ${theme === 'dark' ? 'dark-theme' : ''}`}>
        <div className="error-state">
          <p>❌ {error}</p>
          <button onClick={loadTeamsData} className="retry-btn" type="button">
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`admin-panel ${theme === 'dark' ? 'dark-theme' : ''}`}>
      <div className="admin-header">
        <div className="admin-header-top">
          <div className="admin-header-brand">
            <span className="admin-header-icon">⚕</span>
            <div>
              <h2>Админ-панель</h2>
              <p className="admin-header-subtitle">Управление и аналитика</p>
            </div>
          </div>
          {onBackToMain && (
            <button
              className="back-to-main-btn"
              onClick={onBackToMain}
              title="Вернуться к основному интерфейсу"
              type="button"
            >
              <span className="back-to-main-arrow">←</span>
              К приложению
            </button>
          )}
        </div>
        <nav className="admin-navigation" aria-label="Разделы админ-панели">
          {navItems.map(({ id, icon, label }) => (
            <button
              key={id}
              type="button"
              className={`admin-nav-btn ${currentSection === id ? 'active' : ''}`}
              onClick={() => setCurrentSection(id)}
            >
              <span className="admin-nav-icon">{icon}</span>
              <span className="admin-nav-label">{label}</span>
            </button>
          ))}
        </nav>
      </div>

      {error && (
        <div className="admin-inline-error">
          <p>⚠️ {error}</p>
        </div>
      )}

      {currentSection === 'analytics' && (
        <div className="admin-content">
          <AnalyticsDashboard data={csvData} theme={theme} />
        </div>
      )}

      {currentSection === 'teams' && (
        <TeamsManagement
          teamsData={teamsData}
          onRefresh={loadTeamsData}
          onError={setError}
        />
      )}

      {currentSection === 'settings' && (
        <div className="admin-content">
          <SettingsPanel />
        </div>
      )}

      {currentSection === 'testing' && (
        <div className="admin-content">
          <TestPanel />
        </div>
      )}

      {currentSection === 'dashboard' && (
        <AdminOverviewSection
          teamsData={teamsData}
          theme={theme}
          csvData={csvData}
          onRefresh={loadTeamsData}
        />
      )}
    </div>
  );
};

export default AdminPanel;
